import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation, useRouteMatch, withRouter } from 'react-router';
import { Layout, Row, Col, Button, message, Drawer } from 'antd';
import { InvoiceFormBase, createBuyerInvoiceConfig } from '../InvoiceFormBase';
import OWPdfViewer from '../ow_pdf_viewer/OWPdfViewer';
import {
	invoicesRestCrudThunksForBuyer,
	fetchTaxLineItemsForBuyer,
	getInvoiceNumberBuyer,
} from '../../thunks/invoices_thunks';
import { workOrdersRestCrudThunksForBuyer } from '../../thunks/work_orders_thunks';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import { withUserType } from '../withUserType';
import { getLinkWIthBackLinkParams } from '../../utils/HistoryUtils';
import { getFixedFloatStringOrZero } from '../../utils/InvoiceUtils';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import ClearErrors from '../clear_errors/ClearErrors';
import { INVOICES_CRUD_ACTION_CREATORS } from '../../actions/invoices_actions';
import { ArrowLeftOutlined } from '@ant-design/icons';
import ResizableSplitPanel from '../common/ResizableSplitPanel';

const { Content } = Layout;

const EditBuyerInvoicePageV2: FC<any> = ({
	getInvoice,
	getWorkOrder,
	updateInvoice,
	fetchTaxLineItems,
	checkInvoiceNumber,
	userType,
	currentUser,
	updating,
	updateErrors,
}): React.ReactElement => {
	const match = useRouteMatch<{ id: string; workOrderId?: string }>();
	const history = useHistory();
	const location = useLocation();
	const invoiceId = match.params.id;
	const workOrderIdFromRoute = match.params.workOrderId;

	const [invoice, setInvoice] = useState<any>(null);
	const [workOrder, setWorkOrder] = useState<any>(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		if (!invoiceId) return;
		setLoading(true);
		getInvoice(invoiceId)
			.then((inv) => {
				setInvoice(inv || null);
				const woId = (inv && inv.workOrderId) || workOrderIdFromRoute;
				if (woId) {
					return getWorkOrder(woId).then((wo) => {
						setWorkOrder(wo || null);
					});
				}
				setWorkOrder(null);
			})
			.catch(() => setInvoice(null))
			.finally(() => setLoading(false));
	}, [invoiceId, workOrderIdFromRoute, getInvoice, getWorkOrder]);

	const workOrderId = (invoice && invoice.workOrderId) || workOrderIdFromRoute;

	const onSuccess = useCallback(
		(rec) => {
			history.replace(
				getLinkWIthBackLinkParams(
					{
						...location,
						pathname: workOrderId
							? `/${userType}/workOrders/detail/${workOrderId}/invoices`
							: `/${userType}/invoices/detail/${rec.id}`,
					},
					workOrderId ? 'Back to Work Order' : 'Back to Invoice',
					`/${userType}/invoices/detail/${rec.id}`
				)
			);
		},
		[history, userType, location, workOrderId]
	);

	const handleSubmit = useCallback(
		async (values, formInstance, scope) => {
			if (!invoice || !workOrderId) return;

			const allFormValues = formInstance.getFieldsValue(true);
			const formValues = { ...allFormValues, ...values };

			const hasLineItems = (items: any) => items && Array.isArray(items) && items.length > 0;

			const laborLineItemsArray = formValues.laborLineItems || [];
			const laborLineItems = hasLineItems(laborLineItemsArray)
				? laborLineItemsArray.map((item: any) => ({
						roleType: item.roleType || '',
						numberOfTech: Number(item.numberOfTech || 0),
						seconds: Number(item.seconds || 0),
						hourlyRate: String(item.hourlyRate || 0),
						amount: String(item.amount || 0),
				  }))
				: [];

			const partsLineItemsArray = formValues.partsLineItems || [];
			const partsLineItems = hasLineItems(partsLineItemsArray)
				? partsLineItemsArray.map((item: any, index: number) => ({
						description: item.description || '',
						partId: item.partId || index + 1,
						partNumber: item.partNumber || '',
						unitType: item.unitType || '',
						quantity: item.quantity ? String(item.quantity) : '',
						unitPrice: item.unitPrice ? String(item.unitPrice) : '',
						amount: String(item.amount || 0),
				  }))
				: [];

			const equipmentLineItemsArray = formValues.equipmentLineItems || [];
			const equipmentLineItems = hasLineItems(equipmentLineItemsArray)
				? equipmentLineItemsArray.map((item: any, index: number) => ({
						description: item.description || '',
						equipmentId: item.equipmentId || index + 1,
						equipmentNumber: item.equipmentNumber || '',
						quantity: '1',
						unitType: 'each',
						unitPrice: item.unitPrice ? String(item.unitPrice) : undefined,
						amount: String(item.amount || 0),
				  }))
				: [];

			const materialLineItems = [...partsLineItems, ...equipmentLineItems];

			const travelLineItemsArray = formValues.travelLineItems || [];
			const travelLineItems = hasLineItems(travelLineItemsArray)
				? travelLineItemsArray.map((item: any) => ({
						description: item.description || '',
						amount: String(item.amount || 0),
						...(item.id && { id: item.id }),
						...(item.unitPrice && { unitPrice: String(item.unitPrice) }),
				  }))
				: [];

			const freightLineItemsArray = formValues.freightLineItems || [];
			const freightLineItems = hasLineItems(freightLineItemsArray)
				? freightLineItemsArray.map((item: any) => ({
						description: item.description || '',
						amount: String(item.amount || 0),
						...(item.id && { id: item.id }),
						...(item.unitPrice && { unitPrice: String(item.unitPrice) }),
				  }))
				: [];

			const miscLineItemsArray = formValues.miscLineItems || [];
			const miscLineItems = hasLineItems(miscLineItemsArray)
				? miscLineItemsArray.map((item: any) => ({
						description: item.description || '',
						amount: String(item.amount || 0),
						...(item.id && { id: item.id }),
						...(item.unitPrice && { unitPrice: String(item.unitPrice) }),
				  }))
				: [];

			const taxKeys = formValues.taxKeys || [];
			const taxLineItems =
				taxKeys.length > 0
					? taxKeys.map((taxKey: number) => ({
							taxType: String(formValues[`tax-${taxKey}-taxType`] || ''),
							amount: getFixedFloatStringOrZero(Number(formValues[`tax-${taxKey}-amount`]) || 0),
					  }))
					: [];

			const dateOfInvoice = formValues.dateOfInvoice
				? formValues.dateOfInvoice.format
					? formValues.dateOfInvoice.format('YYYY-MM-DD')
					: typeof formValues.dateOfInvoice === 'string'
					? formValues.dateOfInvoice
					: null
				: null;

			const dueDate = formValues.dueDate
				? formValues.dueDate.format
					? formValues.dueDate.format('YYYY-MM-DD')
					: typeof formValues.dueDate === 'string'
					? formValues.dueDate
					: null
				: null;

			const laborTotalBeforeTax = getFixedFloatStringOrZero(Number(formValues.laborTotal) || 0);
			const materialTotalBeforeTax = getFixedFloatStringOrZero(
				(Number(formValues.partsTotal) || 0) + (Number(formValues.equipmentTotal) || 0)
			);
			const travelTotalBeforeTax = getFixedFloatStringOrZero(Number(formValues.travelTotal) || 0);
			const freightTotalBeforeTax = getFixedFloatStringOrZero(Number(formValues.freightTotal) || 0);
			const miscTotalBeforeTax = getFixedFloatStringOrZero(Number(formValues.miscTotal) || 0);
			const invoiceTotalBeforeTax = getFixedFloatStringOrZero(
				Number(formValues.invoiceTotalBeforeTax) || 0
			);
			const invoiceTax = getFixedFloatStringOrZero(Number(formValues.invoiceTax) || 0);
			const invoiceTotalAfterTax = getFixedFloatStringOrZero(
				Number(formValues.invoiceTotalAfterTax) || 0
			);

			const payload = {
				id: formValues.id,
				scope: scope !== null && scope !== undefined ? scope : '',
				externalWorkOrderId: formValues.externalWorkOrderId
					? `${formValues.externalWorkOrderId}`
					: `${workOrderId}`,
				invoiceNumber: formValues.invoiceNumber || '',
				paymentTerms: formValues.paymentTerms || '',
				dueDate,
				dateOfInvoice,
				invoiceTaxRate: formValues.invoiceTaxRate || null,
				invoiceTax,
				invoiceTotalBeforeTax,
				invoiceTotalAfterTax,
				laborTotalBeforeTax,
				laborLineItems,
				...(taxLineItems.length > 0 && { taxLineItems }),
				materialTotalBeforeTax,
				materialLineItems,
				freightTotalBeforeTax,
				freightLineItems,
				travelTotalBeforeTax,
				travelLineItems,
				miscTotalBeforeTax,
				miscLineItems,
				workOrderId: parseInt(String(workOrderId), 10),
				createdBy: currentUser.email,
				attachments: formValues.attachments || [],
				status: formValues.status || 'pending',
				invoicePDFLink: invoice.invoicePDFLink || undefined,
				isDeleted: invoice && invoice.isDeleted ? true : false,
				buyerFacilityId: formValues.buyerFacilityId || nullSafeGet('facility.id', currentUser),
				supplierFacilityId: invoice && invoice.supplierFacilityId,
			};

			try {
				const record = await updateInvoice(payload);
				if (onSuccess) onSuccess(record);
			} catch (err) {
				if (
					typeof err === 'string' &&
					err.indexOf('Invoice(s) are already associated with this work order') === 0
				) {
					message.error(err);
					history.goBack();
				}
				throw err;
			}
		},
		[invoice, workOrderId, currentUser, updateInvoice, onSuccess, history]
	);

	const formConfig = useMemo(() => {
		if (!invoice || !workOrder || !currentUser) return null;
		return createBuyerInvoiceConfig(invoice, workOrder, currentUser, history, handleSubmit, {
			updating,
			updateErrors,
			onSuccess,
			fetchTaxLineItems,
			checkInvoiceNumber,
		});
	}, [
		invoice,
		workOrder,
		currentUser,
		history,
		handleSubmit,
		updating,
		updateErrors,
		onSuccess,
		fetchTaxLineItems,
		checkInvoiceNumber,
	]);

	const handleBack = useCallback(() => {
		if (workOrderId) {
			history.push(`/${userType}/workOrders/detail/${workOrderId}/invoices`);
		} else {
			history.push(`/${userType}/invoices/detail/${invoiceId}`);
		}
	}, [history, userType, workOrderId, invoiceId]);

	if (loading || !invoice) {
		return <PageLoadingPlaceholder />;
	}

	return (
		<Content>
			<Drawer
				width="100vw"
				placement="right"
				visible={true}
				style={{ top: 0 }}
				bodyStyle={{
					padding: 0,
					height: 'calc(100vh - 67px)',
					overflow: 'hidden',
				}}
				className="fullscreen-drawer"
				closeIcon={
					<Button type="ghost" icon={<ArrowLeftOutlined />} onClick={handleBack}>
						Back
					</Button>
				}
				title={<div className="h3medium">Edit Invoice</div>}
			>
				<Row style={{ height: '100%' }}>
					<ClearErrors actionCreators={INVOICES_CRUD_ACTION_CREATORS} />
					<Col span={24} style={{ height: '100%' }}>
						<ResizableSplitPanel
							leftPanel={
								<div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
									<OWPdfViewer
										file={invoice.invoicePDFLink || null}
										fileName="Invoice.pdf"
										showDelete={false}
									/>
								</div>
							}
							rightPanel={<div>{formConfig && <InvoiceFormBase config={formConfig} />}</div>}
							rightPanelClassName="flex-1 min-h-0 overflow-y-auto p-6 pt-7"
						/>
					</Col>
				</Row>
			</Drawer>
		</Content>
	);
};

const mapStateToProps = (state: any) => ({
	currentUser: state.session.currentUser,
	updating: state.invoices.updating,
	updateErrors: state.invoices.updateErrors,
});

const mapDispatchToProps = (dispatch: any) => ({
	getInvoice: (id: string) => dispatch(invoicesRestCrudThunksForBuyer.readOne(id)),
	getWorkOrder: (id: string) => dispatch(workOrdersRestCrudThunksForBuyer.readOne(id)),
	updateInvoice: (entity: any) => dispatch(invoicesRestCrudThunksForBuyer.update(entity)),
	fetchTaxLineItems: (currencyId: string) => dispatch(fetchTaxLineItemsForBuyer(currencyId)),
	checkInvoiceNumber: (invoice: string, supplierFacilityId: string) =>
		dispatch(getInvoiceNumberBuyer(invoice, supplierFacilityId)),
});

export default withUserType(
	withRouter(connect(mapStateToProps, mapDispatchToProps)(EditBuyerInvoicePageV2))
);
